import React, { useCallback, useEffect, useMemo } from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import { useSubscriptionInfo } from "@editor/hooks/subscription";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import usePendingShopifyIntegrationCookie from "@editor/hooks/usePendingShopifyIntegrationCookie";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { trpc, trpcUtils } from "@editor/utils/trpc";
import { ReploIconBlack } from "@svg/replo-icon-black";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffectEvent } from "replo-utils/react/use-effect-event";
import { BILLING_PLAN_CONFIRMATION_PARAM, BillingTiers } from "schemas/billing";

import BasicShopInfo from "./BasicShopInfo";
import ChoosePlan from "./ChoosePlan";
import ConnectYourStore from "./ConnectYourStore";

export interface OnboardingStepProps {
  currentStep: number;
  submitStep: () => void;
  isLoading: boolean;
  totalSteps: number;
}

// NOTE (Cole, 2025-04-16): This is the core structure of the onboarding flow - if you want to add a new step, just put it here. Order matters. Every step should handle all its own state and simply call the submitStep function when it's ready to move to the next step.
const baseOnboardingSteps = [
  {
    slug: "setup",
    component: ConnectYourStore,
  },
  {
    slug: "workspace-details",
    component: BasicShopInfo,
  },
] as const;

const selectPlanStep = {
  slug: "select-plan",
  component: ChoosePlan,
} as const;

const OnboardingLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-neutral-soft flex h-screen w-full items-stretch justify-center md:p-6">
      <div className="md:border-border shadow-button-selectable relative h-full w-full overflow-hidden border bg-white md:rounded-[24px]">
        <div className="absolute left-4 top-4 hidden md:left-8 md:top-8 md:block">
          <ReploIconBlack className="h-6 w-6 md:h-8 md:w-8" />
        </div>
        {children}
      </div>
    </div>
  );
};

const Onboarding: React.FC<React.PropsWithChildren> = ({ children }) => {
  // NOTE (Ben O., 2025-05-11): This is a temporary state to prevent the user from being redirected to the home page when they complete the onboarding flow
  const [justCompletedOnboarding, setJustCompletedOnboarding] =
    React.useState(false);

  const { onboardingStep } = useParams<{ onboardingStep?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const isWorkspaceOwner = useIsWorkspaceOwner(workspaceId);
  const { subscriptionInfo: currentPlan } = useSubscriptionInfo(workspaceId);
  const isFreeTier = currentPlan?.tier === BillingTiers.FREE;

  const onboardingSteps = useMemo(
    () =>
      isWorkspaceOwner && isFreeTier
        ? [...baseOnboardingSteps, selectPlanStep]
        : baseOnboardingSteps,
    [isWorkspaceOwner, isFreeTier],
  );

  const currentStepIndex = onboardingSteps.findIndex(
    (step) => step.slug === onboardingStep,
  );

  const navigate = useNavigate();
  const { pendingShopifyIntegrationCookie } =
    usePendingShopifyIntegrationCookie();

  const { mutate: updateStatusMutation, isPending: isLoading } =
    trpc.onboarding.updateOnboardingStatus.useMutation();
  const { mutate: setUpInitialWorkspaceAndProject } =
    trpc.onboarding.setUpInitialWorkspaceAndProject.useMutation();

  const isUsingNewOnboarding = isFeatureEnabled("new-onboarding");

  const completeOnboarding = useCallback(() => {
    updateStatusMutation(
      { onboardingCompletedAt: new Date() },
      {
        onSuccess: ({ project }) => {
          setJustCompletedOnboarding(true);
          // NOTE (Ben O., 2025-05-13): Kinda redundant, but we need to call this to ensure the user's workspace is created
          // in scenarios where the user started the old onboarding flow when we didn't initially create a workspace for them
          setUpInitialWorkspaceAndProject();
          void trpcUtils.user.get.invalidate().then(() => {
            logEvent("onboarding.flow.completed", {
              stepsData: { projectId: project?.id },
              whatToDo: [],
            });

            localStorage.removeItem("onboarding-firstName");
            localStorage.removeItem("onboarding-lastName");
            localStorage.removeItem("onboarding-shopName");
            localStorage.removeItem("onboarding-teamDescription");
            localStorage.removeItem("onboarding-annualRevenue");
            localStorage.removeItem("onboarding-setup-selected-option");

            navigate(`/editor/${project?.id}/new`);
          });
        },
        onError: (error) => {
          console.error("Failed to update onboarding status:", error);
        },
      },
    );
  }, [
    updateStatusMutation,
    setUpInitialWorkspaceAndProject,
    logEvent,
    navigate,
  ]);

  const onBeforeUnload = useEffectEvent(() => {
    if (onboardingStep) {
      // If there's a pending Shopify integration cookie, the user is being redirected to Shopify
      // and not actually abandoning the flow
      const isUserAbandoningFlow =
        onboardingStep === "setup"
          ? !Boolean(pendingShopifyIntegrationCookie)
          : true;

      if (isUserAbandoningFlow) {
        logEvent("onboarding.flow.abandoned", {
          lastStepCompleted: onboardingStep,
          stepsData: {},
          whatToDo: [],
        });
      }
    }
  });

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [onBeforeUnload]);

  // TODO (Cole, 2025-04-17): Remove this when we remove the feature flag
  useEffect(() => {
    if (!isUsingNewOnboarding) {
      navigate("/");
    }
  }, [navigate, isUsingNewOnboarding]);

  useEffect(() => {
    if (!justCompletedOnboarding && user?.onboardingCompletedAt) {
      navigate("/home");
    }
  }, [navigate, justCompletedOnboarding, user?.onboardingCompletedAt]);

  // NOTE (Cole, 2025-04-17): Navigate to step 1 if given step is not in mapping
  useEffect(() => {
    const isValidStep =
      onboardingStep &&
      onboardingSteps.some((step) => step.slug === onboardingStep);

    if (!isValidStep) {
      // Navigate to the first step's slug if the current step is invalid
      navigate(`/onboarding/${onboardingSteps[0].slug}`, {
        replace: true,
      });
    }
  }, [onboardingStep, navigate, onboardingSteps]);

  // NOTE (Berkley, 2025-05-21): This useEffect handles detecting changes to billing confirmation params when the user upgrades their plan
  // during onboarding flow. If the URL has a billing confirmation param, we want to handle the step change by either completing onboarding
  // or moving to the next step in the flow. This should be kept seperate from submitStep() to avoid race conditions.
  useEffect(() => {
    const billingConfirmation = searchParams.get(
      BILLING_PLAN_CONFIRMATION_PARAM,
    );

    if (billingConfirmation === "true" && onboardingStep === "select-plan") {
      // NOTE (Berkley, 2025-05-20): We want to clear the billing confirmation param so that we don't handle it multiple times
      const newParams = new URLSearchParams(searchParams);
      newParams.delete(BILLING_PLAN_CONFIRMATION_PARAM);
      setSearchParams(newParams, { replace: true });

      // NOTE (Berkley, 2025-05-20): Instead of using the dynamic onboardingSteps which might have changed
      // after the plan upgrade, use the full set of possible steps
      const allPossibleSteps = [...baseOnboardingSteps, selectPlanStep];
      const stepIndex = allPossibleSteps.findIndex(
        (step) => step.slug === "select-plan",
      );

      if (stepIndex === -1) {
        return;
      }

      const isLastStep = stepIndex === allPossibleSteps.length - 1;

      if (isLastStep) {
        completeOnboarding();
      } else {
        const nextStep = allPossibleSteps[stepIndex + 1];
        if (nextStep) {
          navigate(`/onboarding/${nextStep.slug}`);
        } else {
          console.error(
            "No next step found in onboarding flow. Previous step was:",
            allPossibleSteps[stepIndex],
          );
        }
      }
    }
  }, [
    searchParams,
    onboardingStep,
    navigate,
    setSearchParams,
    completeOnboarding,
  ]);

  // NOTE (Berkley, 2025-05-20): This is the function that is called when the user clicks the "Next" button
  const submitStep = () => {
    if (currentStepIndex === -1) {
      navigate(`/onboarding/${onboardingSteps[0].slug}`, {
        replace: true,
      });
      return;
    }

    const isLastStep = currentStepIndex === onboardingSteps.length - 1;

    if (isLastStep) {
      completeOnboarding();
    } else {
      const nextStep = onboardingSteps[currentStepIndex + 1];
      if (nextStep) {
        navigate(`/onboarding/${nextStep.slug}`);
      }
    }
  };

  const currentStepDetails = onboardingStep
    ? onboardingSteps.find((step) => step.slug === onboardingStep)
    : null;

  const StepComponent = currentStepDetails
    ? currentStepDetails.component
    : null;

  if (!StepComponent) {
    return null;
  }

  return (
    <OnboardingLayout>
      <StepComponent
        currentStep={currentStepIndex + 1}
        submitStep={submitStep}
        isLoading={isLoading}
        totalSteps={onboardingSteps.length}
      />
      {children}
    </OnboardingLayout>
  );
};

export default Onboarding;
